import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const verificationAttemptRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const emailParam = searchParams.get('email');

    if (!token || !emailParam) {
      setStatus('error');
      setMessage('Invalid verification link. Missing parameters.');
      return;
    }

    const attemptKey = `${token}:${emailParam}`;
    if (verificationAttemptRef.current === attemptKey) {
      return;
    }
    verificationAttemptRef.current = attemptKey;

    const cachedResult = sessionStorage.getItem(`verify-result:${attemptKey}`);
    if (cachedResult) {
      const parsed = JSON.parse(cachedResult);
      setStatus(parsed.status);
      setMessage(parsed.message);
      setEmail(parsed.email || emailParam);
      return;
    }

    const verifyEmail = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await axios.get(`${API_URL}/auth/verify-email`, {
          params: { token, email: emailParam },
        });
        const result = {
          status: 'success',
          message: response.data.message,
          email: emailParam,
        };
        sessionStorage.setItem(`verify-result:${attemptKey}`, JSON.stringify(result));
        setStatus(result.status);
        setMessage(result.message);
        setEmail(result.email);
      } catch (err) {
        const result = {
          status: 'error',
          message: err.response?.data?.error || 'Verification failed. The link may have expired.',
          email: err.response?.data?.email || emailParam,
        };
        sessionStorage.setItem(`verify-result:${attemptKey}`, JSON.stringify(result));
        setStatus(result.status);
        setMessage(result.message);
        setEmail(result.email);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async () => {
    if (!email) return;

    setResendLoading(true);
    setResendMessage('');
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { email });
      setResendMessage(response.data.message);
    } catch (err) {
      setResendMessage(err.response?.data?.error || 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex items-center justify-center px-4 font-login">
      <div className="bg-white border border-border rounded-menu p-8 max-w-md w-full text-center shadow-input">
        <img src="/vughylogo.svg" alt="Vughy" className="h-8 w-auto mx-auto mb-6" />
        {status === 'verifying' && (
          <div className="py-8">
            <div className="w-16 h-16 bg-primary-soft rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-ink mb-2">Activating Your Account</h2>
            <p className="text-ink-secondary">Please wait while we activate your account...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-8">
            <h2 className="text-xl font-semibold text-ink mb-2">Account Activated!</h2>
            <p className="text-ink-secondary mb-6">{message}</p>
            <Link to="/login" className="inline-block btn-primary">
              Go to Login
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="py-8">
            <h2 className="text-xl font-semibold text-ink mb-2">Activation Failed</h2>
            <p className="text-ink-secondary mb-4">{message}</p>
            {email && (
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="mb-4 inline-block btn-primary disabled:opacity-50"
              >
                {resendLoading ? 'Sending...' : 'Resend activation email'}
              </button>
            )}
            {resendMessage && <p className="text-sm text-primary mb-4">{resendMessage}</p>}
            <div>
              <Link to="/login" className="text-primary font-semibold text-sm">
                Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
